import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { type Litter } from '@/api/entities/litters';
import { useUpdateLitter } from './api/useUpdateLitter';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DogSelect } from '@/components/ui/dog-select';
import { ImageUpload } from '@/components/ui/image-upload';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from '@/hooks/use-toast';
import { COLOR_CHECKBOX_OPTIONS } from '@/shared/constants';

const editLitterSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  litterType: z.enum(['planned', 'upcoming', 'past']).optional(),
  sireId: z.string().min(1, 'Sire is required'),
  damId: z.string().min(1, 'Dam is required'),
  expectedMatingDate: z.string().optional(),
  expectedDueMonth: z.string().optional(),
  expectedDueDate: z.string().optional(),
  expectedPuppyDate: z.string().optional(),
  dobOfPuppies: z.string().optional(),
  limitedRegistrationPrice: z.number().optional(),
  mainRegistrationPrice: z.number().optional(),
  expectedColors: z.array(z.string()).default([]),
  content: z.string().optional(),
  imagesUrls: z.array(z.string()).optional(),
});

type EditLitterFormData = z.infer<typeof editLitterSchema>;

type Props = {
  litter: Litter | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function EditLitterDialog({ litter, open, onOpenChange }: Props) {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm<EditLitterFormData>({
    resolver: zodResolver(editLitterSchema),
    defaultValues: {
      name: '',
      litterType: undefined,
      sireId: '',
      damId: '',
      expectedMatingDate: '',
      expectedDueMonth: '',
      expectedDueDate: '',
      expectedPuppyDate: '',
      dobOfPuppies: '',
      limitedRegistrationPrice: undefined,
      mainRegistrationPrice: undefined,
      expectedColors: [],
      content: '',
      imagesUrls: [],
    },
  });

  const litterType = watch('litterType');
  const expectedColors = watch('expectedColors') || [];

  const mutation = useUpdateLitter({
    config: {
      onSuccess: () => {
        toast({
          title: 'Success',
          description: 'Litter updated successfully',
        });
        onOpenChange(false);
      },
      onError: (error: Error) => {
        console.error('Mutation error:', error);
        toast({
          title: 'Error',
          description: error.message || 'Failed to update litter',
          variant: 'destructive',
        });
      },
    },
  });

  useEffect(() => {
    if (litter && open) {
      const formatDate = (dateStr?: string) => {
        if (!dateStr) return '';
        try {
          const date = new Date(dateStr);
          if (!isNaN(date.getTime())) {
            return date.toISOString().split('T')[0];
          }
        } catch {}
        return '';
      };

      reset({
        name: litter.name || '',
        litterType: litter.litterType || undefined,
        sireId: litter.sireId || litter.sire?.id || '',
        damId: litter.damId || litter.dam?.id || '',
        expectedMatingDate: litter.expectedMatingDate || '',
        expectedDueMonth: litter.expectedDueMonth || '',
        expectedDueDate: formatDate(litter.expectedDueDate),
        expectedPuppyDate: formatDate(litter.expectedPuppyDate),
        dobOfPuppies: formatDate(litter.dobOfPuppies),
        limitedRegistrationPrice: litter.limitedRegistrationPrice || undefined,
        mainRegistrationPrice: litter.mainRegistrationPrice || undefined,
        expectedColors: litter.expectedColors || [],
        content: litter.content || '',
        imagesUrls: litter.imagesUrls || [],
      });
    }
  }, [litter, open, reset]);

  const onSubmit = (data: EditLitterFormData) => {
    if (!litter) {
      toast({
        title: 'Error',
        description: 'No litter selected',
        variant: 'destructive',
      });
      return;
    }

    const apiPayload: Record<string, unknown> = {
      name: data.name,
      sireId: data.sireId,
      damId: data.damId,
      litterType: data.litterType || litter.litterType,
      expectedColors: data.expectedColors || [],
      content: data.content || undefined,
      imagesUrls: data.imagesUrls || undefined,
      limitedRegistrationPrice: data.limitedRegistrationPrice || undefined,
      mainRegistrationPrice: data.mainRegistrationPrice || undefined,
    };

    if (data.litterType === 'planned' || litter.litterType === 'planned') {
      apiPayload.expectedMatingDate = data.expectedMatingDate || undefined;
      apiPayload.expectedDueMonth = data.expectedDueMonth || undefined;
    }
    if (data.litterType === 'upcoming' || litter.litterType === 'upcoming') {
      apiPayload.expectedDueDate = data.expectedDueDate || undefined;
      apiPayload.expectedPuppyDate = data.expectedPuppyDate || undefined;
    }
    if (data.litterType === 'past' || litter.litterType === 'past') {
      apiPayload.dobOfPuppies = data.dobOfPuppies || undefined;
    }

    Object.keys(apiPayload).forEach((key) => {
      if (apiPayload[key] === undefined) {
        delete apiPayload[key];
      }
    });

    mutation.mutate({ id: litter.id, input: apiPayload as Partial<Litter> });
  };

  const handleColorToggle = (colorValue: string, checked: boolean) => {
    const currentColors = expectedColors || [];
    if (checked) {
      if (colorValue === 'any') {
        setValue('expectedColors', ['any']);
      } else {
        const filteredColors = currentColors.filter((c) => c !== 'any');
        if (!filteredColors.includes(colorValue)) {
          setValue('expectedColors', [...filteredColors, colorValue]);
        }
      }
    } else {
      const filteredColors = currentColors.filter((c) => c !== colorValue);
      setValue('expectedColors', filteredColors);
    }
  };

  const currentLitterType = litterType || litter?.litterType;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Litter</DialogTitle>
          <DialogDescription>Update the litter's information below.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">
                Name <span className="text-destructive">*</span>
              </Label>
              <Input id="name" {...register('name')} placeholder="Enter litter name" />
              {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="litterType">Type</Label>
              <Select
                value={currentLitterType || ''}
                onValueChange={(value) =>
                  setValue('litterType', value as 'planned' | 'upcoming' | 'past')
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="planned">Planned</SelectItem>
                  <SelectItem value="upcoming">Upcoming</SelectItem>
                  <SelectItem value="past">Past</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <DogSelect
              value={watch('sireId')}
              onValueChange={(value) => setValue('sireId', value)}
              sex="Male"
              label={
                <>
                  Sire <span className="text-destructive">*</span>
                </>
              }
            />
            {errors.sireId && (
              <p className="text-sm text-destructive col-span-2">{errors.sireId.message}</p>
            )}

            <DogSelect
              value={watch('damId')}
              onValueChange={(value) => setValue('damId', value)}
              sex="Female"
              label={
                <>
                  Dam <span className="text-destructive">*</span>
                </>
              }
            />
            {errors.damId && (
              <p className="text-sm text-destructive col-span-2">{errors.damId.message}</p>
            )}
          </div>

          {/* Planned Litter Fields */}
          {(currentLitterType === 'planned' || !currentLitterType) && (
            <div className="space-y-4 pt-4 border-t">
              <h3 className="text-lg font-semibold">Planned Litter Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="expectedMatingDate">Expected Mating Month</Label>
                  <Input
                    id="expectedMatingDate"
                    {...register('expectedMatingDate')}
                    placeholder="e.g., January 2024"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="expectedDueMonth">Expected Due Month</Label>
                  <Input
                    id="expectedDueMonth"
                    {...register('expectedDueMonth')}
                    placeholder="e.g., March 2024"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Upcoming Litter Fields */}
          {currentLitterType === 'upcoming' && (
            <div className="space-y-4 pt-4 border-t">
              <h3 className="text-lg font-semibold">Upcoming Litter Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <DatePicker control={control} name="expectedDueDate" label="Expected Due Date" />
                <DatePicker
                  control={control}
                  name="expectedPuppyDate"
                  label="Expected Puppy Date"
                />
              </div>
              <ImageUpload
                control={control}
                name="imagesUrls"
                label="Photos"
                keyPrefix="litters"
                description="Upload multiple images (JPG, PNG)"
              />
            </div>
          )}

          {/* Past Litter Fields */}
          {currentLitterType === 'past' && (
            <div className="space-y-4 pt-4 border-t">
              <h3 className="text-lg font-semibold">Past Litter Details</h3>
              <DatePicker control={control} name="dobOfPuppies" label="DOB of Puppies" />
              <ImageUpload
                control={control}
                name="imagesUrls"
                label="Photos"
                keyPrefix="litters"
                description="Upload multiple images (JPG, PNG)"
              />
            </div>
          )}

          {/* Common Fields */}
          <div className="space-y-4 pt-4 border-t">
            {(currentLitterType === 'planned' || currentLitterType === 'upcoming') && (
              <>
                <h3 className="text-lg font-semibold">Additional Information</h3>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="limitedRegistrationPrice">Limited Registration Price</Label>
                    <Input
                      id="limitedRegistrationPrice"
                      type="number"
                      step="0.01"
                      {...register('limitedRegistrationPrice', { valueAsNumber: true })}
                      placeholder="Enter price"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="mainRegistrationPrice">Main Registration Price</Label>
                    <Input
                      id="mainRegistrationPrice"
                      type="number"
                      step="0.01"
                      {...register('mainRegistrationPrice', { valueAsNumber: true })}
                      placeholder="Enter price"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Expected Colors</Label>
                  <div className="space-y-2">
                    {COLOR_CHECKBOX_OPTIONS.map((colorOption) => (
                      <div key={colorOption.value} className="flex items-center gap-2">
                        <Checkbox
                          id={`color-${colorOption.value}`}
                          checked={expectedColors.includes(colorOption.value)}
                          disabled={colorOption.value !== 'any' && expectedColors.includes('any')}
                          onChange={(e) => handleColorToggle(colorOption.value, e.target.checked)}
                          className="mt-0"
                        />
                        <Label
                          htmlFor={`color-${colorOption.value}`}
                          className="font-normal cursor-pointer leading-none"
                        >
                          {colorOption.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="content">Content</Label>
                  <RichTextEditor
                    value={watch('content') || ''}
                    onChange={(content: string) => {
                      setValue('content', content);
                    }}
                    height={500}
                  />
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || mutation.isPending}>
              {isSubmitting || mutation.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
